/* eslint-disable @typescript-eslint/no-unused-vars */
import { AxiosResponse } from 'axios';
import { v4 as uuidv4 } from 'uuid';

import { ApiClient } from '../../ApiClient';
import repository, { fileContent } from './Repository';
import { Heartbeat } from '../../../../models/dataModels';
import {
  UserPreferences,
  ShiftLog,
  UUID,
  TakeOverDetails,
  APIHierarchy,
  CreateShiftLogResponse,
  ReportTemplate,
  ShiftLogDetails,
  UpdateShiftLogResponse,
  ShiftLogStatusChangeResponse,
  StandingOrderActionResponse,
  TaskActionResponse,
  DateTime,
} from '../../dataModels';
import { Alert } from '../../../web/react-native';

const mockRepo = repository;

const createAxiosResponse = (
  data:
    | Heartbeat
    | APIHierarchy
    | UserPreferences
    | ShiftLog
    | ShiftLog[]
    | TakeOverDetails
    | ShiftLogStatusChangeResponse
    | ReportTemplate[]
    | ShiftLogDetails
    | CreateShiftLogResponse
    | UpdateShiftLogResponse[]
    | StandingOrderActionResponse
    | TaskActionResponse,
): AxiosResponse => {
  return {
    data: data,
    status: 200,
    statusText: 'OK',
    config: {},
    headers: {},
  };
};

const getPreferencesForAssetName = (
  assetName: string,
  userPreferences: UserPreferences,
): UserPreferences => {
  userPreferences.assetName = assetName;
  userPreferences.previouslySet = true;
  switch (assetName) {
    case 'TestSiteA':
      userPreferences.path = 'TestSiteA';
      userPreferences.preferences = {
        site: 'TestSiteA',
        businessTeam: '',
        console: '',
        post: '',
      };
      break;
    case 'TestSiteB':
      userPreferences.path = 'TestSiteB';
      userPreferences.preferences = {
        site: 'TestSiteB',
        businessTeam: '',
        console: '',
        post: '',
      };
      break;
    case 'BusTeam1':
      userPreferences.path = 'TestSiteB\\BusTeam1';
      userPreferences.preferences = {
        site: 'TestSiteB',
        businessTeam: 'BusTeam1',
        console: '',
        post: '',
      };
      break;
    case 'Console1':
      userPreferences.path = 'TestSiteB\\BusTeam1\\Console1';
      userPreferences.preferences = {
        site: 'TestSiteB',
        businessTeam: 'BusTeam1',
        console: 'Console1',
        post: '',
      };
      break;
    case 'Post1':
      userPreferences.path = 'TestSiteB\\BusTeam1\\Console1\\Field1';
      userPreferences.preferences = {
        site: 'TestSiteB',
        businessTeam: 'BusTeam1',
        console: 'Console1',
        post: 'Post1',
      };
      break;
  }

  return userPreferences;
};

const getShiftLogsForCurrentPost = (shiftLogs: ShiftLog[], assetName: string): ShiftLog[] => {
  return shiftLogs.filter((shiftLog) => shiftLog.assetName === assetName);
};

const getShiftLogsForCurrentPostAndItsChildren = (
  shiftLogs: ShiftLog[],
  assetName: string,
): ShiftLog[] => {
  return shiftLogs.filter((shiftLog) => shiftLog.path.includes(assetName));
};

const updateFileContentForShiftLog = (
  shiftLogs: ShiftLog[],
  shiftSummaryID: UUID,
  fileContent: string,
): [ShiftLog, number] => {
  const shiftLogIndex = mockRepo.shiftLogs.findIndex(
    (shiftLog) => shiftLog.shiftSummaryID === shiftSummaryID,
  );

  let shiftLog: ShiftLog;
  if (shiftLogs[shiftLogIndex].status === 'InProgress') {
    shiftLog = shiftLogs[shiftLogIndex];
  } else {
    shiftLog = { ...shiftLogs[shiftLogIndex], fileContent: fileContent };
  }
  return [shiftLog, shiftLogIndex];
};

const apiClient: ApiClient = {
  heartBeat(): Promise<AxiosResponse<Heartbeat>> {
    return new Promise((resolve, _reject) => {
      const heartBeat: AxiosResponse<Heartbeat> = createAxiosResponse(mockRepo.heartBeat);
      resolve(heartBeat);
    });
  },

  getAssetHierarchy(): Promise<AxiosResponse<APIHierarchy>> {
    return new Promise((resolve, _reject) => {
      const hierachy: AxiosResponse<APIHierarchy> = createAxiosResponse(mockRepo.hierarchy);
      resolve(hierachy);
    });
  },

  getUserPreferences(siteCode: string): Promise<AxiosResponse<UserPreferences>> {
    return new Promise((resolve, _reject) => {
      const preferences: AxiosResponse<UserPreferences> = createAxiosResponse(mockRepo.preferences);
      resolve(preferences);
    });
  },

  setUserPreferences(siteCode: string, assetName: string): Promise<AxiosResponse<UserPreferences>> {
    mockRepo.preferences.assetName = assetName;
    mockRepo.preferences = getPreferencesForAssetName(assetName, mockRepo.preferences);
    return new Promise((resolve, _reject) => {
      const preferences: AxiosResponse<UserPreferences> = createAxiosResponse(mockRepo.preferences);
      resolve(preferences);
    });
  },

  getShiftLogs({
    assetName,
    showType,
    fileDownload,
    shiftSummaryID,
  }): Promise<AxiosResponse<ShiftLog[]>> {
    let mockResponse: ShiftLog[] = [];

    if (showType === 'current') {
      mockResponse = getShiftLogsForCurrentPost(mockRepo.shiftLogs, assetName);
    } else if (showType === 'all') {
      mockResponse = getShiftLogsForCurrentPostAndItsChildren(mockRepo.shiftLogs, assetName);
    } else if (fileDownload && shiftSummaryID) {
      const [shiftLog, index] = updateFileContentForShiftLog(
        mockRepo.shiftLogs,
        shiftSummaryID,
        fileContent,
      );

      mockRepo.shiftLogs[index] = { ...shiftLog };
      mockResponse = [mockRepo.shiftLogs[index]];
    }
    return new Promise((resolve, _reject) => {
      const shiftLogs: AxiosResponse<ShiftLog[]> = createAxiosResponse(mockResponse);
      resolve(shiftLogs);
    });
  },

  getTakeOverDetails(
    siteCode: string,
    shiftSummaryID: UUID,
  ): Promise<AxiosResponse<TakeOverDetails>> {
    return new Promise((resolve, _reject) => {
      const takeOverResponse: AxiosResponse<TakeOverDetails> = createAxiosResponse(
        mockRepo.genericTakeOverDetailsResponse,
      );
      resolve(takeOverResponse);
    });
  },

  takeOverShiftLog(
    //consider renaming this to takeoverShiftLog
    siteCode: string,
    shiftSummaryID: UUID,
    takeoverComments: string,
  ): Promise<AxiosResponse<ShiftLogStatusChangeResponse>> {
    return new Promise((resolve, _reject) => {
      const index = mockRepo.shiftLogs.findIndex(
        (shiftLog) => shiftLog.shiftSummaryID === shiftSummaryID,
      );
      let shiftLog = mockRepo.shiftLogs[index];
      shiftLog = {
        ...shiftLog,
        status: 'TakenOver',
        takeoverTime: new Date().toDateString(),
        takeoverUser: 'Temp, Tim T',
      };
      mockRepo.shiftLogs[index] = { ...shiftLog };
      const takeOverShiftLogResponse: AxiosResponse<ShiftLogStatusChangeResponse> =
        createAxiosResponse(mockRepo.genericTakeOverShiftLogResponse);
      resolve(takeOverShiftLogResponse);
    });
  },

  submitShiftLog(siteCode: string, shiftSummaryID: UUID): Promise<AxiosResponse<ShiftLog>> {
    const index = mockRepo.shiftLogs.findIndex((s) => s.shiftSummaryID === shiftSummaryID);
    if (index > -1) {
      mockRepo.shiftLogs[index] = {
        ...mockRepo.shiftLogs[index],
        status: 'Submitted',
        submitUser: 'Temp, Tim T',
        submitTime: new Date().toUTCString(),
      };
    }
    return new Promise((resolve, _reject) => {
      const statusChangeResponse: AxiosResponse<ShiftLog> = createAxiosResponse(
        mockRepo.genericSubmitShiftLogResponse,
      );
      resolve(statusChangeResponse);
    });
  },

  unsubmitShiftLog(siteCode: string, shiftSummaryID: UUID): Promise<AxiosResponse<ShiftLog>> {
    return new Promise((resolve, _reject) => {
      // TODO: Handle unsubmissinon of a shift log in the mock repo & replace response with mock.genericUnsubmitShiftLogResponse
      const statusChangeResponse: AxiosResponse<ShiftLog> = createAxiosResponse({
        status: false,
        message: 'Unsubmitting the shift log was not possible',
      });
      resolve(statusChangeResponse);
    });
  },

  getReportTemplates(
    siteCode: string,
    assetName: string,
  ): Promise<AxiosResponse<ReportTemplate[]>> {
    return new Promise((resolve, _reject) => {
      // TODO: Add a more inteligent template list? Maybe? Maybe not.
      const reportTemplatesResponse: AxiosResponse<ReportTemplate[]> = createAxiosResponse(
        mockRepo.reportTemplates,
      );

      resolve(reportTemplatesResponse);
    });
  },

  createShiftLog(
    siteCode: string,
    assetName: string,
    templateId: string,
  ): Promise<AxiosResponse<CreateShiftLogResponse>> {
    return new Promise((resolve) => {
      const shiftSummaryId = +new Date() + '';
      const currentTime = new Date().toUTCString();
      const shiftLog: ShiftLog = {
        shiftSummaryID: shiftSummaryId,
        templateName: templateId,
        assetName: assetName,
        path: mockRepo.preferences.path,
        createUser: 'Temp, Tim T',
        createTime: currentTime,
        submitUser: '',
        submitTime: '',
        takeoverUser: '',
        takeoverTime: '',
        approveUser: '',
        approveTime: '',
        shiftStart: '2021-08-11T17:30:00.0000000Z',
        shiftEnd: '2021-08-11T19:30:00.0000000Z',
        status: 'InProgress',
        takeoverDisplay: false,
        filename: '',
        fileStatus: 'NoDownload',
        fileContent: null,
      };
      const key = Object.keys(mockRepo.sampleShiftLogDetails)[0];
      const snippets = mockRepo.sampleShiftLogDetails[key].snippets.map((s) => ({
        ...s,
        shiftsummaryDisplyId: s.shiftsummaryDisplyId + shiftSummaryId,
      }));

      mockRepo.shiftLogs = [shiftLog, ...mockRepo.shiftLogs];
      mockRepo.sampleShiftLogDetails[shiftSummaryId] = {
        ...mockRepo.sampleShiftLogDetails[key],
        shiftSummaryID: shiftSummaryId,
        snippets,
      };
      const createShiftLogResponse: AxiosResponse<CreateShiftLogResponse> = createAxiosResponse({
        ...shiftLog,
        snippets: mockRepo.sampleShiftLogDetails[shiftSummaryId].snippets,
      });

      resolve(createShiftLogResponse);
    });
  },

  updateShiftLog(
    siteCode: string,
    assetName: string,
    shiftLogDetails: ShiftLogDetails,
  ): Promise<AxiosResponse<ShiftLogDetails>> {
    return new Promise((resolve, _reject) => {
      mockRepo.sampleShiftLogDetails[shiftLogDetails.shiftSummaryID] = {
        ...shiftLogDetails,
        snippets: mockRepo.sampleShiftLogDetails[shiftLogDetails.shiftSummaryID].snippets.map((s) =>
          s.shiftsummaryDisplyId === shiftLogDetails.snippets[0].shiftsummaryDisplyId
            ? shiftLogDetails.snippets[0]
            : s.groupSnippets
            ? {
                ...s,
                groupSnippets: s.groupSnippets.map((g) =>
                  g.shiftsummaryDisplyId !== shiftLogDetails.snippets[0].shiftsummaryDisplyId
                    ? g
                    : shiftLogDetails.snippets[0],
                ),
              }
            : s,
        ),
      };

      resolve(createAxiosResponse(mockRepo.sampleShiftLogDetails[shiftLogDetails.shiftSummaryID]));
    });
  },

  getShiftLogDetails(
    siteCode: string,
    shiftSummaryID: UUID,
  ): Promise<AxiosResponse<ShiftLogDetails>> {
    return new Promise((resolve, _reject) => {
      const shiftLogDetails: AxiosResponse<ShiftLogDetails> = createAxiosResponse(
        mockRepo.sampleShiftLogDetails[shiftSummaryID],
      );
      resolve(shiftLogDetails);
    });
  },

  activateStandingOrder(
    siteCode: string,
    assetName: string,
    displayTemplateId: UUID,
    standingOrderId: UUID,
  ): Promise<AxiosResponse<StandingOrderActionResponse>> {
    return new Promise((resolve, _reject) => {
      const standingOrderActionResponse: AxiosResponse<StandingOrderActionResponse> =
        createAxiosResponse({
          standingOrderId: standingOrderId,
          statusDisplayName: 'Activated',
        });
      const key = Object.values(mockRepo.sampleShiftLogDetails).find((v) =>
        v.snippets.find((s) => s.displayTemplateId === displayTemplateId),
      ).shiftSummaryID;
      mockRepo.sampleShiftLogDetails[key] = updateShiftLogDetailsFields(
        mockRepo.sampleShiftLogDetails[key],
        { key: 'standingOrderId', value: standingOrderId },
        [{ key: 'statusDisplayName', value: 'Activated' }],
      );
      resolve(standingOrderActionResponse);
    });
  },

  completeStandingOrder(
    siteCode: string,
    assetName: string,
    displayTemplateId: UUID,
    standingOrderId: UUID,
  ): Promise<AxiosResponse<StandingOrderActionResponse>> {
    return new Promise((resolve, _reject) => {
      const standingOrderActionResponse: AxiosResponse<StandingOrderActionResponse> =
        createAxiosResponse({
          standingOrderId: standingOrderId,
          statusDisplayName: 'Completed',
        });
      const key = Object.values(mockRepo.sampleShiftLogDetails).find((v) =>
        v.snippets.find((s) => s.displayTemplateId === displayTemplateId),
      ).shiftSummaryID;

      mockRepo.sampleShiftLogDetails[key] = updateShiftLogDetailsFields(
        mockRepo.sampleShiftLogDetails[key],
        { key: 'standingOrderId', value: standingOrderId },
        [{ key: 'statusDisplayName', value: 'Completed' }],
      );
      resolve(standingOrderActionResponse);
    });
  },

  postStandingOrderComment(
    siteCode: string,
    standingOrderId: UUID,
    comment: string,
  ): Promise<AxiosResponse<never>> {
    return new Promise((resolve, _reject) => {
      const postStandingOrderCommentResponse = createAxiosResponse(undefined) as any;
      resolve(postStandingOrderCommentResponse);
    });
  },

  startTask(
    siteCode: string,
    assetName: string,
    displayTemplateId: UUID,
    taskId: UUID,
    startTime: DateTime,
  ): Promise<AxiosResponse<TaskActionResponse>> {
    return new Promise((resolve, _reject) => {
      const key = Object.values(mockRepo.sampleShiftLogDetails).find((v) =>
        v.snippets.find((s) => s.displayTemplateId === displayTemplateId),
      ).shiftSummaryID;
      const snippet = repository.sampleShiftLogDetails[key].snippets.find(
        (s) => s.displayTemplateId === displayTemplateId,
      );
      const task = snippet.data[0].rowData.find((r) => r.taskId === taskId);

      const taskActionResponse: AxiosResponse<TaskActionResponse> = createAxiosResponse({
        ...task,
        state: 'In-Progress',
        actualStartTime: startTime,
      } as TaskActionResponse);
      mockRepo.sampleShiftLogDetails[key] = updateShiftLogDetailsFields(
        mockRepo.sampleShiftLogDetails[key],
        { key: 'taskId', value: taskId },
        [
          { key: 'state', value: 'In-Progress' },
          { key: 'actualStartTime', value: startTime },
        ],
      );
      resolve(taskActionResponse);
    });
  },

  pauseTask(
    siteCode: string,
    assetName: string,
    displayTemplateId: UUID,
    taskId: UUID,
  ): Promise<AxiosResponse<TaskActionResponse>> {
    return new Promise((resolve, _reject) => {
      const key = Object.values(mockRepo.sampleShiftLogDetails).find((v) =>
        v.snippets.find((s) => s.displayTemplateId === displayTemplateId),
      ).shiftSummaryID;
      const snippet = repository.sampleShiftLogDetails[key].snippets.find(
        (s) => s.displayTemplateId === displayTemplateId,
      );
      const task = snippet.data[0].rowData.find((r) => r.taskId === taskId);

      const taskActionResponse: AxiosResponse<TaskActionResponse> = createAxiosResponse({
        ...task,
        state: 'Paused',
      } as TaskActionResponse);
      mockRepo.sampleShiftLogDetails[key] = updateShiftLogDetailsFields(
        mockRepo.sampleShiftLogDetails[key],
        { key: 'taskId', value: taskId },
        [{ key: 'state', value: 'Paused' }],
      );
      resolve(taskActionResponse);
    });
  },

  resumeTask(
    siteCode: string,
    assetName: string,
    displayTemplateId: UUID,
    taskId: UUID,
  ): Promise<AxiosResponse<TaskActionResponse>> {
    return new Promise((resolve, _reject) => {
      const key = Object.values(mockRepo.sampleShiftLogDetails).find((v) =>
        v.snippets.find((s) => s.displayTemplateId === displayTemplateId),
      ).shiftSummaryID;
      const snippet = repository.sampleShiftLogDetails[key].snippets.find(
        (s) => s.displayTemplateId === displayTemplateId,
      );
      const task = snippet.data[0].rowData.find((r) => r.taskId === taskId);

      const taskActionResponse: AxiosResponse<TaskActionResponse> = createAxiosResponse({
        ...task,
        state: 'In-Progress',
      } as TaskActionResponse);
      mockRepo.sampleShiftLogDetails[key] = updateShiftLogDetailsFields(
        mockRepo.sampleShiftLogDetails[key],
        { key: 'taskId', value: taskId },
        [{ key: 'state', value: 'In-Progress' }],
      );
      resolve(taskActionResponse);
    });
  },

  completeTask(
    siteCode: string,
    assetName: string,
    displayTemplateId: UUID,
    taskId: UUID,
    startTime: DateTime,
    endTime: DateTime,
  ): Promise<AxiosResponse<TaskActionResponse>> {
    return new Promise((resolve, _reject) => {
      const key = Object.values(mockRepo.sampleShiftLogDetails).find((v) =>
        v.snippets.find((s) => s.displayTemplateId === displayTemplateId),
      ).shiftSummaryID;
      const snippet = repository.sampleShiftLogDetails[key].snippets.find(
        (s) => s.displayTemplateId === displayTemplateId,
      );
      const task = snippet.data[0].rowData.find((r) => r.taskId === taskId);

      const taskActionResponse: AxiosResponse<TaskActionResponse> = createAxiosResponse({
        ...task,
        state: 'Completed',
        actualStartTime: startTime,
        actualEndTime: endTime,
        completionPercent: '100',
      } as TaskActionResponse);
      mockRepo.sampleShiftLogDetails[key] = updateShiftLogDetailsFields(
        mockRepo.sampleShiftLogDetails[key],
        { key: 'taskId', value: taskId },
        [
          { key: 'state', value: 'Completed' },
          { key: 'actualStartTime', value: startTime },
          { key: 'actualEndTime', value: endTime },
          { key: 'completionPercent', value: '100' },
        ],
      );
      resolve(taskActionResponse);
    });
  },

  cancelTask(
    siteCode: string,
    assetName: string,
    displayTemplateId: UUID,
    taskId: UUID,
  ): Promise<AxiosResponse<TaskActionResponse>> {
    return new Promise((resolve, _reject) => {
      const key = Object.values(mockRepo.sampleShiftLogDetails).find((v) =>
        v.snippets.find((s) => s.displayTemplateId === displayTemplateId),
      ).shiftSummaryID;
      const snippet = repository.sampleShiftLogDetails[key].snippets.find(
        (s) => s.displayTemplateId === displayTemplateId,
      );
      const task = snippet.data[0].rowData.find((r) => r.taskId === taskId);

      const taskActionResponse: AxiosResponse<TaskActionResponse> = createAxiosResponse({
        ...task,
        state: 'Cancelled',
      } as TaskActionResponse);
      mockRepo.sampleShiftLogDetails[key] = updateShiftLogDetailsFields(
        mockRepo.sampleShiftLogDetails[key],
        { key: 'taskId', value: taskId },
        [{ key: 'state', value: 'Cancelled' }],
      );
      resolve(taskActionResponse);
    });
  },

  updatePercentage(
    siteCode: string,
    assetName: string,
    displayTemplateId: string,
    taskId: UUID,
    percentCompleted: number,
  ): Promise<AxiosResponse<TaskActionResponse>> {
    return new Promise((resolve, _reject) => {
      const key = Object.values(mockRepo.sampleShiftLogDetails).find((v) =>
        v.snippets.find((s) => s.displayTemplateId === displayTemplateId),
      ).shiftSummaryID;
      const snippet = repository.sampleShiftLogDetails[key].snippets.find(
        (s) => s.displayTemplateId === displayTemplateId,
      );
      const task = snippet.data[0].rowData.find((r) => r.taskId === taskId);

      const taskActionResponse: AxiosResponse<TaskActionResponse> = createAxiosResponse({
        ...task,
        completionPercent: percentCompleted + '',
      } as TaskActionResponse);
      mockRepo.sampleShiftLogDetails[key] = updateShiftLogDetailsFields(
        mockRepo.sampleShiftLogDetails[key],
        { key: 'taskId', value: taskId },
        [{ key: 'completionPercent', value: Math.round(percentCompleted) + '' }],
      );
      resolve(taskActionResponse);
    });
  },

  postTaskComment(
    siteCode: string,
    assetName: string,
    taskId: UUID,
    comment: string,
  ): Promise<AxiosResponse<never>> {
    return new Promise((resolve, _reject) => {
      resolve(createAxiosResponse(undefined) as any);
    });
  },
};

export default apiClient;

const updateShiftLogDetailsFields = (
  shiftLogDetails: ShiftLogDetails,
  id: { key: string; value: string },
  fields: { key: string; value: string }[],
) => ({
  ...shiftLogDetails,
  snippets: mockRepo.sampleShiftLogDetails[shiftLogDetails.shiftSummaryID].snippets.map((s) => ({
    ...s,
    data:
      s.data?.map((d) => ({
        ...d,
        rowData: d.rowData.map((r) =>
          r[id.key] === id.value
            ? Object.assign({}, r, ...fields.map((field) => ({ [field.key]: field.value })))
            : r,
        ),
      })) ?? null,
  })),
});
